import { useState, useEffect } from 'react';
import { supabase } from '../config/supabase';

/**
 * Hook to fetch Dashboard data from Supabase
 * Returns: farm stats, sales, costs, production, activities, available years
 */
export const useDashboardData = (farmIds = [], selectedYear = null) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [availableYears, setAvailableYears] = useState([]);

  useEffect(() => {
    if (!farmIds || farmIds.length === 0) {
      setLoading(false);
      return;
    }
    loadDashboardData();
  }, [farmIds.join(','), selectedYear]); // Re-fetch when farm or year changes

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('📊 Loading dashboard data for farms:', farmIds);

      // 1. Get farm details including size from farm table
      const { data: farms, error: farmError } = await supabase
        .from('farm')
        .select('id, farmName, farmsize')
        .in('id', farmIds);

      if (farmError) {
        console.error('❌ Error fetching farm details:', farmError);
        throw farmError;
      }
      
      console.log('✅ Farm details loaded:', farms?.length, 'farms', farms?.map(f => `${f.farmName}: ${f.farmsize} acres`));

      // 1B. Get season/cycle count from season table with harvest data
      const { data: seasons, error: seasonError } = await supabase
        .from('season')
        .select('id, farm, produce_name, name_season, expected_harvest_amount, expected_harvest_amount_unit, date_start')
        .in('farm', farmIds)
        .eq('is_soft_deleted', false);
      
      if (seasonError) {
        console.warn('⚠️ Season data error:', seasonError.message);
      }
      
      const seasonCount = seasons?.length || 0;
      console.log('🌱 Seasons/cycles found:', seasonCount);
      
      // Get top produce from seasons with harvest data
      // Filter by year if specified
      let seasonsForProduce = seasons || [];
      
      if (selectedYear) {
        seasonsForProduce = seasonsForProduce.filter(s => {
          if (!s.date_start) return false;
          const seasonYear = new Date(s.date_start).getFullYear();
          return seasonYear === selectedYear;
        });
      }
      
      const seasonsWithHarvest = seasonsForProduce.filter(s => 
        s.expected_harvest_amount && s.expected_harvest_amount > 0
      ) || [];
      
      // Group by produce and sum expected harvest
      const produceMap = {};
      const colors = ['#10b981', '#f59e0b', '#3b82f6', '#ef4444', '#8b5cf6']; // green, amber, blue, red, purple
      
      seasonsWithHarvest.forEach(season => {
        const produceId = season.produce_name || 'Unknown';
        const name = season.name_season || `Produce ${produceId}`;
        const amount = season.expected_harvest_amount || 0;
        
        if (!produceMap[name]) {
          produceMap[name] = { name, weight: 0, count: 0 };
        }
        produceMap[name].weight += amount;
        produceMap[name].count += 1;
      });
      
      // Convert to array and sort by weight
      const productionData = Object.values(produceMap)
        .sort((a, b) => b.weight - a.weight)
        .slice(0, 5) // Top 5
        .map((item, index) => ({
          ...item,
          color: colors[index % colors.length]
        }));
      
      console.log('🌾 Top produce:', productionData, 'for year:', selectedYear || 'all');

      // 2. Get financial transactions for selected farms
      // IMPORTANT: Filter by farm FIRST, then get data
      const { data: allTransactions, error: transactionError } = await supabase
        .from('financial_transaction')
        .select('*')
        .in('farm', farmIds); // Filter by farm IDs BEFORE fetching

      if (transactionError) {
        console.error('❌ Transaction data error:', transactionError.message);
        console.error('   Error details:', transactionError);
      }
      
      console.log('✅ Fetched transactions for farms:', farmIds, '→', allTransactions?.length || 0, 'transactions');
      
      // Log first transaction to see what columns exist
      if (allTransactions && allTransactions.length > 0) {
        console.log('💡 Transaction columns:', Object.keys(allTransactions[0]));
        console.log('💡 Sample transaction:', allTransactions[0]);
      }
      
      // Extract available years from all transactions
      const years = [...new Set(
        allTransactions?.map(t => {
          const date = new Date(t.date_transaction || t.created_at);
          return date.getFullYear();
        }).filter(y => !isNaN(y))
      )].sort((a, b) => b - a); // Sort descending (newest first)
      
      setAvailableYears(years);
      console.log('📅 Available years:', years);
      
      // Transactions are already filtered by farm at database level
      // Just assign them directly
      let filteredTransactions = allTransactions || [];
      
      // Filter by year if specified (null = all years)
      if (selectedYear) {
        filteredTransactions = filteredTransactions.filter(t => {
          const txDate = new Date(t.date_transaction || t.created_at);
          return txDate.getFullYear() === selectedYear;
        });
        console.log(`📅 Filtered for year ${selectedYear}:`, filteredTransactions.length, 'transactions');
      } else {
        console.log('📅 Showing all years:', filteredTransactions.length, 'transactions');
      }
      
      console.log('💰 Filtered transactions for farms:', filteredTransactions.length);

      // Separate income and expenses using the 'in' field
      // in: true = Income (money in), in: false = Expense (money out)
      const incomeData = filteredTransactions?.filter(t => t.in === true) || [];
      const expenseData = filteredTransactions?.filter(t => t.in === false) || [];

      console.log('💰 Transactions loaded:', {
        total: allTransactions?.length || 0,
        income: incomeData.length,
        expense: expenseData.length
      });

      // 4. Get recent activities from farm diary
      const { data: activities, error: activityError } = await supabase
        .from('farm_diary')
        .select('*')
        .in('farm_id', farmIds)
        .order('created_at', { ascending: false })
        .limit(10);

      if (activityError) console.warn('Activities error:', activityError.message);

      // Calculate statistics (use absolute values for amounts)
      const totalSales = incomeData?.reduce((sum, t) => sum + Math.abs(t.amount || 0), 0) || 0;
      const totalCost = expenseData?.reduce((sum, t) => sum + Math.abs(t.amount || 0), 0) || 0;
      const netProfit = totalSales - totalCost;
      const profitMargin = totalSales > 0 ? ((netProfit / totalSales) * 100).toFixed(2) : 0;
      
      console.log('📊 Calculated totals:', {
        sales: totalSales,
        cost: totalCost,
        profit: netProfit,
        margin: profitMargin
      });

      // Calculate farm statistics from actual data
      const totalSize = farms?.reduce((sum, f) => sum + (f.farmsize || 0), 0) || 0;
      const avgSize = farms?.length > 0 ? (totalSize / farms.length).toFixed(2) : 0;

      // Calculate additional metrics
      const farmCount = farms?.length || 1; // Avoid division by zero
      const avgSalesPerFarm = farmCount > 0 ? (totalSales / farmCount).toFixed(2) : 0;
      const avgCostPerFarm = farmCount > 0 ? (totalCost / farmCount).toFixed(2) : 0;
      const avgProfitPerFarm = farmCount > 0 ? (netProfit / farmCount).toFixed(2) : 0;
      const avgOrdersPerFarm = farmCount > 0 ? Math.round(incomeData.length / farmCount) : 0;
      
      // Calculate margin per kg (need harvest weight data)
      // For now, calculate based on profit margin per transaction
      const avgMarginPerKg = totalSales > 0 ? (netProfit / totalSales).toFixed(2) : 0;

      const dashboardData = {
        farm: {
          count: farmCount,
          totalSize: totalSize,
          avgSize: avgSize,
          season: seasonCount // Calculated from transaction season data
        },
        sales: {
          total: totalSales,
          avgOrderSize: incomeData?.length > 0 ? (totalSales / incomeData.length).toFixed(2) : 0,
          avgWeight: incomeData?.length > 0 ? ((totalSales / incomeData.length) / 10).toFixed(2) : 0, // Estimated: assuming RM10/kg average
          avgPerFarm: avgSalesPerFarm
        },
        cost: {
          total: totalCost,
          netProfit: netProfit,
          profitMargin: profitMargin,
          avgPerFarm: avgCostPerFarm,
          avgProfitPerFarm: avgProfitPerFarm
        },
        metrics: {
          avgMarginPerKg: avgMarginPerKg,
          avgOrdersPerFarm: avgOrdersPerFarm
        },
        production: productionData,
        activities: activities || []
      };

      setData(dashboardData);
      console.log('✅ Dashboard data loaded:', dashboardData);

    } catch (err) {
      console.error('❌ Error loading dashboard data:', err);
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  return { data, loading, error, availableYears, refresh: loadDashboardData };
};

/**
 * Hook to fetch Financial Analytics data from Supabase
 * Returns: detailed financial breakdown with date filtering
 */
export const useAnalyticsData = (farmIds = [], startDate = null, endDate = null) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!farmIds || farmIds.length === 0) {
      setLoading(false);
      return;
    }
    loadAnalyticsData();
  }, [farmIds.join(','), startDate, endDate]);

  const loadAnalyticsData = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('💰 Loading analytics data for farms:', farmIds, 'from', startDate, 'to', endDate);

      // Build query for all transactions with date filter
      let query = supabase
        .from('financial_transaction')
        .select('*')
        .in('farm', farmIds)
        .order('date_transaction', { ascending: false });

      if (startDate) query = query.gte('date_transaction', startDate);
      if (endDate) query = query.lte('date_transaction', endDate);

      const { data: transactions, error: txError } = await query;

      if (txError) throw txError;

      console.log('✅ Analytics transactions loaded:', transactions?.length || 0);

      // Separate income and expenses based on 'in' field (true = income, false = expense)
      const income = transactions?.filter(t => t.in === true) || [];
      const expenses = transactions?.filter(t => t.in === false) || [];

      // Calculate totals
      const totalSales = income.reduce((sum, t) => sum + (t.amount || 0), 0);
      const totalCost = expenses.reduce((sum, t) => sum + (t.amount || 0), 0);
      const netProfit = totalSales - totalCost;
      const profitMargin = totalSales > 0 ? ((netProfit / totalSales) * 100).toFixed(2) : 0;

      // Calculate monthly averages (assuming date range)
      const months = startDate && endDate 
        ? Math.max(1, Math.ceil((new Date(endDate) - new Date(startDate)) / (1000 * 60 * 60 * 24 * 30)))
        : 9;

      const avgSalesPerMonth = months > 0 ? (totalSales / months).toFixed(2) : 0;
      const avgCostPerMonth = months > 0 ? (totalCost / months).toFixed(2) : 0;
      const avgOrderPerMonth = months > 0 ? (income.length / months).toFixed(2) : 0;

      // Per unit calculations
      const avgOrderSize = income.length > 0 ? (totalSales / income.length).toFixed(2) : 0;
      const avgPricePerKg = 3.16; // TODO: Calculate from actual weight data
      const avgCostPerKg = 31.16; // TODO: Calculate from actual weight data
      const avgMarginPerKg = (avgPricePerKg - avgCostPerKg).toFixed(2);

      // Cost breakdown by subtype (based on financial_transaction_subtype ID)
      const costsBySubtype = {};
      expenses.forEach(t => {
        const subtypeId = t.financial_transaction_subtype || 0;
        const subtypeKey = `Subtype_${subtypeId}`;
        costsBySubtype[subtypeKey] = (costsBySubtype[subtypeKey] || 0) + (t.amount || 0);
      });

      const analyticsData = {
        totalSales,
        totalCost,
        netProfit,
        profitMargin,
        totalHarvest: 0, // TODO: Get from harvest table
        totalSold: 0, // TODO: Get from harvest table
        totalOrders: income.length,
        developmentCost: costsBySubtype['Development'] || 0,
        labourCost: costsBySubtype['Labour'] || 0,
        farmInputCost: (costsBySubtype['Seed'] || 0) + (costsBySubtype['Fertilizer'] || 0) + (costsBySubtype['Pesticide'] || 0),
        variableCost: costsBySubtype['Variable'] || 0,
        avgSalesPerMonth,
        avgCostPerMonth,
        avgOrderPerMonth,
        avgOrderSize,
        avgPricePerKg,
        avgCostPerKg,
        avgMarginPerKg,
        transactions: transactions || [],
        costsBySubtype
      };

      setData(analyticsData);
      console.log('✅ Analytics data loaded');

    } catch (err) {
      console.error('❌ Error loading analytics data:', err);
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  return { data, loading, error, refresh: loadAnalyticsData };
};

/**
 * Hook to fetch Utilisation data from Supabase
 * Returns: material usage records with filtering
 */
export const useUtilisationData = (
  farmIds = [], 
  startDate = null, 
  endDate = null,
  farmFilter = '',
  materialFilter = ''
) => {
  const [data, setData] = useState([]);
  const [stats, setStats] = useState({ totalInputTypes: 0, totalActivities: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!farmIds || farmIds.length === 0) {
      setLoading(false);
      return;
    }
    loadUtilisationData();
  }, [farmIds.join(','), startDate, endDate, farmFilter, materialFilter]);

  const loadUtilisationData = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('🌾 Loading utilisation data for farms:', farmIds);

      // Query inventory_transaction for material usage (in=false means materials used/going out)
      // Use simple query first, then fetch related data separately to avoid nested join limits
      let query = supabase
        .from('inventory_transaction')
        .select('id, date_transact, amount, description, farm, inventory_item')
        .eq('in', false)
        .order('date_transact', { ascending: false, nullsFirst: false })
        .range(0, 9999);

      // Only filter by farm if specific farms selected (not all farms)
      if (farmIds.length > 0 && farmIds.length <= 500) {
        query = query.in('farm', farmIds);
      }
      console.log('🌾 Farm filter applied:', farmIds.length <= 500 ? 'Yes' : 'No (all farms)');

      if (startDate) query = query.gte('date_transact', startDate);
      if (endDate) query = query.lte('date_transact', endDate);

      const { data: records, error: recordsError } = await query;

      if (recordsError) throw recordsError;

      console.log('✅ Utilisation records loaded:', records?.length || 0);

      // Get unique inventory_item IDs
      const inventoryItemIds = [...new Set(records?.map(r => r.inventory_item).filter(Boolean) || [])];
      
      // Fetch inventory items with material data
      let materialMap = {};
      if (inventoryItemIds.length > 0) {
        // Batch fetch in chunks of 500 to avoid limits
        for (let i = 0; i < inventoryItemIds.length; i += 500) {
          const chunk = inventoryItemIds.slice(i, i + 500);
          const { data: inventoryItems } = await supabase
            .from('inventory_item')
            .select(`
              id,
              material (
                id,
                name_material,
                material_type (
                  id,
                  type_name
                )
              )
            `)
            .in('id', chunk);
          
          inventoryItems?.forEach(ii => {
            materialMap[ii.id] = ii.material;
          });
        }
      }

      // Get unique farm IDs and fetch farm names
      const uniqueFarmIds = [...new Set(records?.map(r => r.farm).filter(Boolean) || [])];
      let farmMap = {};
      if (uniqueFarmIds.length > 0) {
        for (let i = 0; i < uniqueFarmIds.length; i += 500) {
          const chunk = uniqueFarmIds.slice(i, i + 500);
          const { data: farms } = await supabase
            .from('farm')
            .select('id, farmName')
            .in('id', chunk);
          
          farms?.forEach(f => {
            farmMap[f.id] = f.farmName;
          });
        }
      }

      // Transform data to match UI format
      const transformedData = records?.map(item => {
        const material = materialMap[item.inventory_item];
        const materialType = material?.material_type;
        
        return {
          date: item.date_transact ? new Date(item.date_transact).toLocaleDateString() : '-',
          category: materialType?.type_name || 'General',
          material: material?.name_material || 'N/A',
          amount: item.amount ? `${item.amount}` : '-',
          farm: farmMap[item.farm] || 'Unknown',
          description: item.description || '-',
          materialId: material?.id
        };
      }) || [];

      // Calculate statistics
      const totalInputTypes = new Set(transformedData.map(item => item.material).filter(m => m !== 'N/A')).size;
      const totalActivities = transformedData.length;

      setData(transformedData);
      setStats({ totalInputTypes, totalActivities });
      console.log('✅ Utilisation data loaded:', totalActivities, 'records');

    } catch (err) {
      console.error('❌ Error loading utilisation data:', err);
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  return { data, stats, loading, error, refresh: loadUtilisationData };
};

export default {
  useDashboardData,
  useAnalyticsData,
  useUtilisationData
};
